import React, { useContext, useMemo } from 'react'
import { useTranslations } from 'next-intl'
import { Heading, Paragraph } from '../../atoms/Typography/Typography.component'
import { Row, Col, SpacerBase } from '../../atoms/GridSystem'
import Input from '../../atoms/Fields/Input/Input.component'
import Select from '../../atoms/Fields/Select/Select.component'
import * as S from './LeadForm.styles'
import { SectionThemeContext } from '../../../contexts/SectionTheme.context'

type LeadFormStep1Props = {
  withBackground?: boolean
}

const PHONE_PREFIX_CODES = [
  { key: 'italy', value: '+39' },
  { key: 'uk', value: '+44' },
  { key: 'germany', value: '+49' },
  { key: 'france', value: '+33' },
  { key: 'spain', value: '+34' },
  { key: 'switzerland', value: '+41' },
  { key: 'austria', value: '+43' },
] as const

function LeadFormStep1({ withBackground }: LeadFormStep1Props) {
  const { selectedColorScheme } = useContext(SectionThemeContext)
  const t = useTranslations('leadForm.step1')

  const subjectOptions = useMemo(() => [
    { label: t('subjectOptions.info'), value: 'info' },
    { label: t('subjectOptions.commercial'), value: 'commercial' },
    { label: t('subjectOptions.other'), value: 'other' },
  ], [t])

  const phonePrefixOptions = useMemo(() =>
    PHONE_PREFIX_CODES.map(({ key, value }) => ({
      label: `${value} (${t(`phonePrefixOptions.${key}`)})`,
      value,
    })),
  [t])

  return (
    <>
      <S.LeadFormStepTitleWrapper colorScheme={selectedColorScheme}>
        <Heading typo="headingXXL" as="h2">{t('title')}</Heading>
        <Paragraph typo="paragraphMD" content={t('description')} />
      </S.LeadFormStepTitleWrapper>

      <Row>
        <Col xs={12} lg={4}>
          <S.FieldWrapper>
            <Input
              name="firstName"
              id="firstName"
              type="text"
              label={t('firstNameLabel')}
              required
              aria-required
              withFeedback={false}
              uppercase={false}
              password={false}
              withBackground={withBackground}
            />
          </S.FieldWrapper>
        </Col>
        <Col xs={12} lg={4}>
          <S.FieldWrapper>
            <Input
              name="lastName"
              id="lastName"
              type="text"
              label={t('lastNameLabel')}
              required
              aria-required
              withFeedback={false}
              uppercase={false}
              password={false}
              withBackground={withBackground}
            />
          </S.FieldWrapper>
        </Col>
        <Col xs={12} lg={4}>
          <S.FieldWrapper>
            <Input
              name="email"
              id="email"
              type="email"
              label={t('emailLabel')}
              helperText={t('emailHelper')}
              required
              aria-required
              withFeedback={false}
              uppercase={false}
              password={false}
              withBackground={withBackground}
            />
          </S.FieldWrapper>
        </Col>
      </Row>

      <Row>
        <Col xs={12} lg={6}>
          <S.FieldWrapper>
            <Select
              name="subject"
              id="subject"
              label={t('subjectLabel')}
              placeholder={t('subjectPlaceholder')}
              helperText={t('subjectHelper')}
              options={subjectOptions}
              required
              withBackground={withBackground}
            />
          </S.FieldWrapper>
        </Col>
        <Col xs={12} lg={2}>
          <S.FieldWrapper>
            <Select
              name="phonePrefix"
              id="phonePrefix"
              label={t('phonePrefixLabel')}
              helperText={t('phonePrefixHelper')}
              options={phonePrefixOptions}
              required
              withBackground={withBackground}
            />
          </S.FieldWrapper>
        </Col>
        <Col xs={12} lg={4}>
          <S.FieldWrapper>
            <Input
              name="phone"
              id="phone"
              type="tel"
              label={t('phoneLabel')}
              placeholder=""
              required
              aria-required
              withFeedback={false}
              uppercase={false}
              password={false}
              withBackground={withBackground}
            />
          </S.FieldWrapper>
        </Col>
      </Row>
    </>
  )
}

export default LeadFormStep1
